/**
 * Demographics — sex, age and location of a Manifold tenant's active patients,
 * against the ACS 2023 adult population. Optionally narrowed to patients
 * created in a date range, and segmented by acquisition channel, drug, current
 * dose level or weight-loss goal: one segment filters the page, a second is
 * compared beside it.
 */

import { useEffect, useState } from 'react'
import { format, subDays } from 'date-fns'
import { RefreshCw } from 'lucide-react'
import { demographicsAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { DateRangePicker } from '../components/DateRangePicker'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui'
import SexAgeChart from '../components/demographics/SexAgeChart'
import PatientMap from '../components/demographics/PatientMap'
import TreatmentMix from '../components/demographics/TreatmentMix'
import { STATE_NAMES } from '../utils/usStates'
import AttributionSettings, { DEFAULT_ENGINE } from '../components/AttributionSettings'
import { DEFAULT_LOOKBACK } from '../lib/attributionLookback'

const controlClass = 'h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground'
const count = v => Number(v || 0).toLocaleString('en-US')
const pct = v => (v == null ? '–' : `${(v * 100).toFixed(1)}%`)
const times = v => (v == null ? '–' : `${v.toFixed(2)}×`)
const per100k = v => (v == null ? '–' : v.toFixed(v < 10 ? 2 : 1))

function Segmented({ value, onChange, options, label }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-md border border-input p-0.5">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={`rounded px-3 py-1 text-sm ${value === o.value ? 'bg-secondary font-medium text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** Share of the group's sex-and-age-known patients who are this sex. */
const sexShare = (group, sex) => (group.sexAgeKnown
  ? group.sexAge.reduce((sum, cell) => sum + cell[sex].patients, 0) / group.sexAgeKnown
  : null)

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  )
}

const ZIP_LIMIT = 100

const adultsLabel = v => {
  if (v == null) return '–'
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`
  if (v >= 1e4) return `${Math.round(v / 1e3)}K`
  return count(v)
}

function SortHeader({ label, sortKey, sort, onSort, align = 'right' }) {
  const active = sort.key === sortKey
  return (
    <th className={`py-2 pr-3 font-medium ${align === 'right' ? 'text-right' : ''}`} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => onSort(sortKey)} className={`inline-flex items-center gap-1 hover:text-foreground ${active ? 'text-foreground' : ''}`}>
        {label}
        <span aria-hidden className="w-2 text-[10px]">{active ? (sort.dir === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  )
}

/**
 * One group's places at one level, as {key → {name, sub, adults, patients,
 * share, per100k, index}}. ZIPs get a share of the patients a ZIP could be
 * named for, and no census index: at one or two patients per ZIP it is noise.
 */
function placesFor(group, level) {
  if (level === 'states') {
    return new Map(group.states.map(s => [s.state, { ...s, name: STATE_NAMES[s.state] || s.state }]))
  }
  if (level === 'metros') {
    return new Map(group.metros.map(m => [m.cbsa, { ...m, sub: m.type === 'micro' ? 'micro' : null }]))
  }
  const located = group.coverage.zctaLocated || 0
  return new Map(group.zips.map(z => [z.zip, {
    ...z, name: z.zip, sub: z.metro, share: located ? z.patients / located : null, index: null,
  }]))
}

/** A place the other group has and this one does not: zero patients, same census. */
function absentLike(row) {
  return {
    ...row,
    patients: 0,
    share: 0,
    per100k: row.adults ? 0 : null,
    index: row.index == null ? null : row.adults ? 0 : null,
  }
}

const LEVELS = [
  { value: 'states', label: 'States', noun: 'State' },
  { value: 'metros', label: 'Metros', noun: 'Metro' },
  { value: 'zips', label: `Top ${ZIP_LIMIT} ZIPs`, noun: 'ZIP' },
]

function GeoTable({ groups }) {
  const [level, setLevel] = useState('states')
  const [showAll, setShowAll] = useState(false)
  const [sort, setSort] = useState({ key: 'a.patients', dir: 'desc' })
  const [a, b] = groups
  const aPlaces = placesFor(a, level)
  const bPlaces = b ? placesFor(b, level) : null
  const withIndex = level !== 'zips'

  let rows = [...new Set([...aPlaces.keys(), ...(bPlaces ? bPlaces.keys() : [])])].map(key => {
    const ar = aPlaces.get(key) || absentLike(bPlaces.get(key))
    const br = bPlaces ? (bPlaces.get(key) || absentLike(ar)) : null
    const delta = br && ar.share != null && br.share != null ? (ar.share - br.share) * 100 : null
    return { key, name: ar.name, sub: ar.sub, a: ar, b: br, delta }
  })

  if (level === 'states' && !showAll) rows = rows.filter(r => r.a.patients > 0 || r.b?.patients > 0)
  if (level === 'zips') {
    // The top ZIPs by patients across both groups, then sorted as the user asks.
    rows = rows
      .sort((x, y) => (y.a.patients + (y.b?.patients || 0)) - (x.a.patients + (x.b?.patients || 0)) || x.key.localeCompare(y.key))
      .slice(0, ZIP_LIMIT)
  }

  // Keys are 'name', 'delta', or '<group>.<field>'. Missing values sort last
  // in either direction, so a place with no census row never tops a column.
  const valueOf = (row, key) => {
    if (key === 'name') return row.name
    if (key === 'delta') return row.delta
    const [group, field] = key.split('.')
    return row[group]?.[field] ?? null
  }
  rows.sort((x, y) => {
    const vx = valueOf(x, sort.key)
    const vy = valueOf(y, sort.key)
    if (vx == null && vy == null) return 0
    if (vx == null) return 1
    if (vy == null) return -1
    const cmp = typeof vx === 'string' ? vx.localeCompare(vy) : vx - vy
    return sort.dir === 'asc' ? cmp : -cmp
  })

  const onSort = key => setSort(prev => (prev.key === key
    ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: key === 'name' ? 'asc' : 'desc' }))
  const changeLevel = value => {
    setLevel(value)
    // The index column does not exist for ZIPs; fall back rather than sort by nothing.
    if (value === 'zips' && /index/.test(sort.key)) setSort({ key: 'a.patients', dir: 'desc' })
  }
  const h = (label, key, align) => <SortHeader label={label} sortKey={key} sort={sort} onSort={onSort} align={align} />
  const noun = LEVELS.find(l => l.value === level).noun

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium">By {noun.toLowerCase()}</h3>
        <Segmented label="Table level" value={level} onChange={changeLevel} options={LEVELS} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm tabular-nums">
          <thead className="text-left text-xs text-muted-foreground">
            <tr className="border-b">
              {h(noun, 'name', 'left')}
              {h('Adults', 'a.adults')}
              {h(b ? `${a.label} pts` : 'Patients', 'a.patients')}
              {h('Share', 'a.share')}
              {h('Per 100k', 'a.per100k')}
              {withIndex && h('vs pop.', 'a.index')}
              {b && <>
                {h(`${b.label} pts`, 'b.patients')}
                {h('Share', 'b.share')}
                {withIndex && h('vs pop.', 'b.index')}
                {h('Share Δ', 'delta')}
              </>}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, name, sub, a: x, b: y, delta }) => (
              <tr key={key} className="border-b last:border-0">
                <td className="py-1.5 pr-3">
                  {name}
                  {sub && <span className="ml-2 text-xs text-muted-foreground">{sub}</span>}
                </td>
                <td className="py-1.5 pr-3 text-right text-muted-foreground">{adultsLabel(x.adults)}</td>
                <td className="py-1.5 pr-3 text-right">{count(x.patients)}</td>
                <td className="py-1.5 pr-3 text-right">{pct(x.share)}</td>
                <td className="py-1.5 pr-3 text-right">{per100k(x.per100k)}</td>
                {withIndex && <td className="py-1.5 pr-3 text-right">{times(x.index)}</td>}
                {b && <>
                  <td className="py-1.5 pr-3 text-right">{count(y?.patients)}</td>
                  <td className="py-1.5 pr-3 text-right">{pct(y?.share)}</td>
                  {withIndex && <td className="py-1.5 pr-3 text-right">{times(y?.index)}</td>}
                  <td className="py-1.5 pr-3 text-right">{delta == null ? '–' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)} pp`}</td>
                </>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {level === 'states' && (
        <button type="button" className="text-xs text-muted-foreground underline-offset-2 hover:underline" onClick={() => setShowAll(v => !v)}>
          {showAll ? 'Hide states with no patients' : 'Show every state'}
        </button>
      )}
      {level === 'metros' && (
        <p className="text-xs text-muted-foreground">Metros with at least one patient. Share is of patients whose ZIP is in the census; those outside any metro stay in that total.</p>
      )}
      {level === 'zips' && (
        <p className="text-xs text-muted-foreground">The {ZIP_LIMIT} ZIPs with the most patients{b ? ' across both groups' : ''}. No census index: at a patient or two per ZIP it is noise.</p>
      )}
    </div>
  )
}

export default function DemographicsPage() {
  const { currentClinicId, clinics } = useAuthStore()
  // null = every active patient, regardless of when they were created.
  const [createdRange, setCreatedRange] = useState(null)
  const [dimension, setDimension] = useState('channel')
  const [engine, setEngine] = useState(DEFAULT_ENGINE)
  const lookbackDays = DEFAULT_LOOKBACK // all time for every brand; not a choice on the page
  const [segment, setSegment] = useState('all')
  const [compare, setCompare] = useState('')
  const [histMode, setHistMode] = useState('count')
  const [mapMetric, setMapMetric] = useState('patients')
  const [mapGroup, setMapGroup] = useState(0)
  const [bubbles, setBubbles] = useState('metros')
  const [areas, setAreas] = useState('states')
  const [refresh, setRefresh] = useState(0)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  const startDate = createdRange ? format(createdRange.from, 'yyyy-MM-dd') : ''
  const endDate = createdRange ? format(createdRange.to, 'yyyy-MM-dd') : ''
  const requestKey = `${currentClinicId}:${startDate}:${endDate}:${dimension}:${segment}:${compare}:${engine}:${lookbackDays}:${refresh}`
  const data = result?.key === requestKey ? result.data : null

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)
    if (!currentClinicId) { setLoading(false); return }
    const params = { dimension, segment, engine, lookbackDays, ...(compare ? { compare } : {}), ...(startDate ? { startDate, endDate } : {}) }
    demographicsAPI.getReport(params)
      .then(response => { if (active) setResult({ key: requestKey, data: response.data }) })
      .catch(err => { if (active) setError(err.response?.data?.error?.message || 'Could not load demographics. Try refreshing.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [currentClinicId, startDate, endDate, dimension, segment, compare, engine, lookbackDays, requestKey])

  // A segment picked under another clinic does not exist here.
  useEffect(() => { setSegment('all'); setCompare(''); setMapGroup(0) }, [currentClinicId])
  // Reset in the same render as the dimension change, so no request pairs the
  // new dimension with a value from the old one.
  const changeDimension = value => { setDimension(value); setSegment('all'); setCompare(''); setMapGroup(0) }
  useEffect(() => { if (!compare) setMapGroup(0) }, [compare])

  // The picker list survives a reload of the same clinic so it does not blank out.
  const lastData = result?.key?.startsWith(`${currentClinicId}:`) ? result.data : null
  const dimensions = lastData?.dimensions || [{ value: 'channel', label: 'Channel', allLabel: 'All channels' }]
  const segmentValues = lastData?.segments?.[dimension] || []
  const allLabel = dimensions.find(d => d.value === dimension)?.allLabel || 'Everyone'
  const groups = data?.groups || []
  const primary = groups[0]
  const mapTarget = groups[mapGroup] || primary

  const segmentOptions = [
    { value: 'all', label: allLabel },
    ...segmentValues.map(v => ({ value: v.value, label: `${v.label} (${count(v.patients)})` })),
  ]

  return <div className="flex flex-col gap-6 p-4 sm:p-6">
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">Demographics</h1>
        <p className="text-sm text-muted-foreground">{clinics?.find(c => c.id === currentClinicId)?.name || currentClinicId || 'Select a clinic'} · Who your active patients are and where they live, against US adults.</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <select className={controlClass} aria-label="Segment by" value={dimension} onChange={e => changeDimension(e.target.value)}>
          {dimensions.map(d => <option key={d.value} value={d.value}>By {d.label.toLowerCase()}</option>)}
        </select>
        <select className={controlClass} aria-label="Segment" value={segment} onChange={e => setSegment(e.target.value)}>
          {segmentOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className={controlClass} aria-label="Compare to" value={compare} onChange={e => setCompare(e.target.value)}>
          <option value="">No comparison</option>
          {segmentOptions.filter(o => o.value !== segment).map(o => <option key={o.value} value={o.value}>Compare: {o.label}</option>)}
        </select>
        <select
          className={controlClass}
          aria-label="Patient created"
          value={createdRange ? 'range' : 'all'}
          onChange={e => setCreatedRange(e.target.value === 'all' ? null : { from: subDays(new Date(), 89), to: new Date() })}
        >
          <option value="all">All active patients</option>
          <option value="range">Created in range…</option>
        </select>
        {createdRange && <DateRangePicker dateRange={createdRange} onDateRangeChange={setCreatedRange} />}
        {dimension === 'channel' && <AttributionSettings models={data?.attribution?.models} engine={engine} onEngineChange={setEngine} />}
        <Button variant="outline" size="sm" aria-label="Refresh demographics" disabled={loading || !currentClinicId} onClick={() => setRefresh(v => v + 1)}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
    </header>

    {!currentClinicId ? <p className="text-sm text-muted-foreground">Select a clinic to view its demographics.</p> : <>
      {error && <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{error}</p>}
      {loading && !data && !error && <p className="text-sm text-muted-foreground">Loading demographics…</p>}

      {data && primary && <>
        <p className="text-xs text-muted-foreground">
          Active = Manifold subscription status “active” (paused excluded).
          {data.window ? ` Patients created ${data.window.startDate} through ${data.window.endDate}, Eastern time.` : ' Every active patient.'}
          {' '}Sex, age and weight-loss goal from core patient records; drug and dose level from each patient’s latest core order; location from the newest active subscription’s shipping address. Baseline: {data.census.source}.
        </p>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Active patients" value={count(data.activePatients)} hint={data.window || primary.value != null ? `${count(primary.patients)} in this view` : null} />
          <Stat label="Female" value={pct(sexShare(primary, 'F'))} hint="of patients with sex and age known" />
          <Stat label="Male" value={pct(sexShare(primary, 'M'))} hint="of patients with sex and age known" />
          <Stat label="Located" value={count(primary.coverage.located)} hint={`${count(primary.metros.length)} metros · ${count(primary.coverage.nonMetro)} outside any metro`} />
        </div>

        {groups.some(g => !g.sufficient) && (
          <p className="text-xs text-muted-foreground">Groups under {data.minCohort} patients are marked thin; their shares and indices move a lot with each patient.</p>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Treatment mix</CardTitle>
            <CardDescription>What patients in view are on now, and the goal they gave at intake. Levels are per drug.</CardDescription>
          </CardHeader>
          <CardContent>
            <TreatmentMix groups={groups} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
            <div>
              <CardTitle className="text-lg">Sex and age</CardTitle>
              <CardDescription>
                {histMode === 'census'
                  ? 'Each cell’s share of patients divided by its share of US adults. Above 1× is over-represented.'
                  : histMode === 'share' ? 'Share of the group’s patients in each sex and age band.' : 'Active patients by age band, split by sex.'}
              </CardDescription>
            </div>
            <Segmented
              label="Histogram scale"
              value={histMode}
              onChange={setHistMode}
              options={[{ value: 'count', label: 'Count' }, { value: 'share', label: 'Share' }, { value: 'census', label: 'vs Census' }]}
            />
          </CardHeader>
          <CardContent>
            <SexAgeChart groups={groups} mode={histMode} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
            <div>
              <CardTitle className="text-lg">Where patients live</CardTitle>
              <CardDescription>
                {mapMetric === 'index'
                  ? `${areas === 'metros' ? 'Metro' : 'State'} share of patients ÷ its share of US adults. Gray is proportional to population.`
                  : mapMetric === 'per100k' ? `Patients per 100,000 adults in each ${areas === 'metros' ? 'Census metro or micro area' : 'state'}.` : `Patients per ${areas === 'metros' ? 'Census metro or micro area' : 'state'}.`}
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {groups.length > 1 && (
                <Segmented label="Map group" value={mapGroup} onChange={setMapGroup} options={groups.map((g, i) => ({ value: i, label: g.label }))} />
              )}
              <Segmented
                label="Map metric"
                value={mapMetric}
                onChange={setMapMetric}
                options={[{ value: 'patients', label: 'Patients' }, { value: 'per100k', label: 'Per 100k' }, { value: 'index', label: 'vs Census' }]}
              />
              <Segmented
                label="Map areas"
                value={areas}
                onChange={value => { setAreas(value); if (value === 'metros' && bubbles === 'metros') setBubbles('off') }}
                options={[{ value: 'states', label: 'States' }, { value: 'metros', label: 'Metros' }]}
              />
              <Segmented
                label="Map bubbles"
                value={bubbles}
                onChange={setBubbles}
                options={[{ value: 'metros', label: 'Metros' }, { value: 'zips', label: 'ZIPs' }, { value: 'off', label: 'No bubbles' }]}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <PatientMap group={mapTarget} metric={mapMetric} bubbles={bubbles} areas={areas} nationalAdults={data.census.adults} />
            <GeoTable groups={groups} />
          </CardContent>
        </Card>
      </>}
    </>}
  </div>
}
